/**
 * Mapping utilities between JournalArticlesSidebar options and URL paths.
 *
 * Each sidebar option corresponds to a unique route segment under /articles/.
 * These helpers convert between the `SidebarOption` union type and the
 * URL pathname so the sidebar selection is always reflected in the browser URL.
 */

import {
  SidebarOption,
  DEFAULT_SIDEBAR_OPTION,
  collectionIdFromOption,
} from "@/components/journal-articles/JournalArticlesSidebar"
import { ROUTINE_SURVEILLANCE_TYPE } from "@/services/biblioService"

/** Base path for the journal articles section. */
const BASE_PATH = "/articles"

/**
 * URL segment used for each collection type under /articles/collection/.
 * Each collection type gets its own route so a dedicated page component can be
 * mounted per type (RS extraction / user collection):
 *   - Routine Surveillance      → /articles/collection/rs/:id
 *   - User Collection (default) → /articles/collection/:id
 * User collections use the bare path (no type segment), which also keeps old
 * /articles/collection/:id links working for them. (PR Literature Review
 * cycles moved to the dedicated /pr-literature-review/cycle/:id route.)
 */
const COLLECTION_TYPE_SEGMENT: Partial<Record<string, string>> = {
  [ROUTINE_SURVEILLANCE_TYPE]: "rs",
}

/**
 * Static sidebar options that map 1:1 to a URL segment under /articles/.
 * The default option ("all-journal-articles") maps to the bare /articles path.
 */
const STATIC_OPTION_SEGMENTS: Record<string, SidebarOption> = {
  "recently-added": "recently-added",
  "your-saved-articles": "your-saved-articles",
  "duplicate-articles": "duplicate-articles",
}

/**
 * Topic slug → SidebarOption mapping.
 * URL: /articles/topic/:topicSlug
 */
const TOPIC_SLUG_TO_OPTION: Record<string, SidebarOption> = {
  "parasite-rate": "topic-parasite-rate",
  "parasite-incidence": "topic-parasite-incidence",
  "insecticide-resistance": "topic-insecticide-resistance",
  "seasonality": "topic-seasonality",
}

/**
 * SidebarOption → topic slug (reverse of TOPIC_SLUG_TO_OPTION).
 */
const OPTION_TO_TOPIC_SLUG: Partial<Record<SidebarOption, string>> = {
  "topic-parasite-rate": "parasite-rate",
  "topic-parasite-incidence": "parasite-incidence",
  "topic-insecticide-resistance": "insecticide-resistance",
  "topic-seasonality": "seasonality",
}

/**
 * Converts a SidebarOption into the corresponding URL path.
 *
 * For collection options the resulting path depends on the collection's type,
 * so a type-specific page component can be mounted (see COLLECTION_TYPE_SEGMENT).
 * The optional `collectionType` is only consulted for collection options; pass
 * it whenever the type is known (the sidebar always knows it when building
 * collection links). When omitted for a collection option, the bare
 * user-collection path is produced.
 *
 * @param option - The sidebar option to convert.
 * @param collectionType - The collection's type, for collection options only.
 * @returns The absolute URL path (e.g. "/articles/topic/parasite-rate").
 */
export function sidebarOptionToPath(option: SidebarOption, collectionType?: string): string {
  // Default option maps to the bare base path
  if (option === DEFAULT_SIDEBAR_OPTION) {
    return BASE_PATH
  }

  // Static options (recently-added, open-author-contacts, etc.)
  if (option in STATIC_OPTION_SEGMENTS) {
    return `${BASE_PATH}/${option}`
  }

  // Topic options → /articles/topic/:slug
  const topicSlug = OPTION_TO_TOPIC_SLUG[option]
  if (topicSlug) {
    return `${BASE_PATH}/topic/${topicSlug}`
  }

  // Collection options → /articles/collection[/type]/:id. PR and RS collections
  // get a type segment so their dedicated pages mount; user collections (and
  // the type-unknown fallback) use the bare path.
  const collectionId = collectionIdFromOption(option)
  if (collectionId !== null) {
    const typeSegment: string | undefined =
      collectionType !== undefined ? COLLECTION_TYPE_SEGMENT[collectionType] : undefined
    if (typeSegment !== undefined) {
      return `${BASE_PATH}/collection/${typeSegment}/${collectionId}`
    }
    return `${BASE_PATH}/collection/${collectionId}`
  }

  // Fallback — should not happen with a well-typed SidebarOption
  return BASE_PATH
}

/**
 * Derives the current SidebarOption from the browser's location pathname.
 *
 * @param pathname - The current location.pathname (e.g. "/articles/topic/parasite-rate").
 * @returns The resolved SidebarOption, defaulting to "all-journal-articles" if unrecognized.
 */
export function pathToSidebarOption(pathname: string): SidebarOption {
  // Strip trailing slash for consistency
  const normalized = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname

  // Bare /articles → default
  if (normalized === BASE_PATH) {
    return DEFAULT_SIDEBAR_OPTION
  }

  // /articles/topic/:slug
  const topicMatch = normalized.match(/^\/articles\/topic\/(.+)$/)
  if (topicMatch) {
    const slug = topicMatch[1]
    return TOPIC_SLUG_TO_OPTION[slug] ?? DEFAULT_SIDEBAR_OPTION
  }

  // /articles/collection/:id and the type-specific variant
  // /articles/collection/rs/:id both resolve to the same collection-{id}
  // sidebar option (the type only selects which page component mounts; the
  // sidebar highlight is by id). (PR cycles moved out to
  // /pr-literature-review/cycle/:id.)
  const typedCollectionMatch = normalized.match(/^\/articles\/collection\/rs\/(\d+)$/)
  if (typedCollectionMatch) {
    return `collection-${parseInt(typedCollectionMatch[1], 10)}` as SidebarOption
  }
  const collectionMatch = normalized.match(/^\/articles\/collection\/(\d+)$/)
  if (collectionMatch) {
    return `collection-${parseInt(collectionMatch[1], 10)}` as SidebarOption
  }

  // /articles/:segment — check static options
  const segmentMatch = normalized.match(/^\/articles\/(.+)$/)
  if (segmentMatch) {
    const segment = segmentMatch[1]
    if (segment in STATIC_OPTION_SEGMENTS) {
      return STATIC_OPTION_SEGMENTS[segment]
    }
  }

  return DEFAULT_SIDEBAR_OPTION
}
